import { createApi } from '@reduxjs/toolkit/query/react';
import { getApiBaseUrl } from '../../../Features/API/api';
import { baseQueryWithReauth } from '../../../Features/API/baseQueryWithReauth';

const BASE_URL = getApiBaseUrl();

export const salesApiSlice = createApi({
  reducerPath: 'salesApi',
  baseQuery: baseQueryWithReauth(BASE_URL),
  tagTypes: [
    'SalesOrganization', 'SalesOrgDashboard', 'SalesEvent', 'SalesEventStats',
    'SalesFleet', 'SalesDrivers', 'SalesDashboardSummary', 'SalesTeam',
    'SalesOrgInfo', 'SalesOrgUsers',
    'IntakeForm', 'IntakeDraft', 'IntakeAnalytics',
  ],
  endpoints: (builder) => ({
    // ── Sales Organization Endpoints ──────────────────────────────────
    fetchMyOrganizations: builder.query({
      query: () => 'fleet/sales/my-organizations/',
      providesTags: ['SalesOrganization'],
    }),

    fetchOrgDashboard: builder.query({
      query: (orgId) => `fleet/sales/organization/${orgId}/dashboard/`,
      providesTags: (result, error, orgId) => [
        { type: 'SalesOrgDashboard', id: orgId },
      ],
    }),

    fetchSalesOrgEvents: builder.query({
      query: ({ orgId, ...params }) => ({
        url: `fleet/sales/organization/${orgId}/events/`,
        params: {
          page: params.page || 1,
          page_size: params.pageSize || 25,
          search: params.search || '',
          priority: params.priority || '',
          event_type: params.eventType || '',
          start_date: params.startDate || '',
          end_date: params.endDate || '',
          has_media: params.hasMedia || '',
          media_type: params.mediaType || '',
        },
      }),
      providesTags: ['SalesEvent'],
      keepUnusedDataFor: 30,
    }),

    fetchSalesOrgEventStats: builder.query({
      query: (orgId) => `fleet/sales/organization/${orgId}/events/stats/`,
      providesTags: ['SalesEventStats'],
      keepUnusedDataFor: 60,
    }),

    fetchSalesOrgFleet: builder.query({
      query: (orgId) => `fleet/sales/organization/${orgId}/fleet/`,
      providesTags: (result, error, orgId) => [{ type: 'SalesFleet', id: orgId }],
      keepUnusedDataFor: 60,
    }),

    fetchSalesOrgDrivers: builder.query({
      query: (orgId) => `fleet/sales/organization/${orgId}/drivers/`,
      providesTags: (result, error, orgId) => [{ type: 'SalesDrivers', id: orgId }],
      keepUnusedDataFor: 60,
    }),

    fetchSalesDashboardSummary: builder.query({
      query: (params) => ({
        url: 'fleet/sales/dashboard-summary/',
        params: {
          start_date: params?.startDate || '',
          end_date: params?.endDate || '',
        },
      }),
      providesTags: ['SalesDashboardSummary'],
      keepUnusedDataFor: 120,
    }),

    fetchSalesEventDetail: builder.query({
      query: (eventId) => `fleet/sales/events/${eventId}/`,
      providesTags: (result, error, eventId) => [{ type: 'SalesEvent', id: eventId }],
      keepUnusedDataFor: 60,
    }),

    fetchSalesTeamMembers: builder.query({
      query: () => 'fleet/sales/team/',
      providesTags: ['SalesTeam'],
    }),

    assignRepToAE: builder.mutation({
      query: ({ userId, action, aeId }) => ({
        url: `fleet/sales/team/${userId}/assign/`,
        method: 'PATCH',
        body: { action: action || 'add', ae_id: aeId },
      }),
      invalidatesTags: ['SalesTeam'],
    }),

    // ── General Info & Contacts ──────────────────────────────────────

    fetchOrgInfo: builder.query({
      query: (orgId) => `fleet/sales/organization/${orgId}/info/`,
      providesTags: (result, error, orgId) => [{ type: 'SalesOrgInfo', id: orgId }],
    }),

    updateOrgContacts: builder.mutation({
      query: ({ orgId, contacts, cascadeToGroups }) => ({
        url: `fleet/sales/organization/${orgId}/contacts/`,
        method: 'PUT',
        body: { contacts, cascade_to_groups: cascadeToGroups },
      }),
      invalidatesTags: (result, error, { orgId }) => [
        { type: 'SalesOrgInfo', id: orgId },
        { type: 'SalesOrganization' },
      ],
    }),

    updateGroupContacts: builder.mutation({
      query: ({ orgId, groupId, contacts }) => ({
        url: `fleet/sales/organization/${orgId}/groups/${groupId}/contacts/`,
        method: 'PUT',
        body: { contacts },
      }),
      invalidatesTags: (result, error, { orgId }) => [{ type: 'SalesOrgInfo', id: orgId }],
    }),

    resetGroupContacts: builder.mutation({
      query: ({ orgId, groupId }) => ({
        url: `fleet/sales/organization/${orgId}/groups/${groupId}/contacts/reset/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { orgId }) => [{ type: 'SalesOrgInfo', id: orgId }],
    }),

    // ── User Management ─────────────────────────────────────────────

    fetchOrgUsers: builder.query({
      query: (orgId) => `fleet/sales/organization/${orgId}/users/`,
      providesTags: (result, error, orgId) => [{ type: 'SalesOrgUsers', id: orgId }],
    }),

    createOrgUser: builder.mutation({
      query: ({ orgId, ...body }) => ({
        url: `fleet/sales/organization/${orgId}/users/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { orgId }) => [{ type: 'SalesOrgUsers', id: orgId }],
    }),

    updateOrgUser: builder.mutation({
      query: ({ orgId, userId, ...body }) => ({
        url: `fleet/sales/organization/${orgId}/users/${userId}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { orgId }) => [{ type: 'SalesOrgUsers', id: orgId }],
    }),

    resetUserPassword: builder.mutation({
      query: ({ orgId, userId }) => ({
        url: `fleet/sales/organization/${orgId}/users/${userId}/reset-password/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { orgId }) => [{ type: 'SalesOrgUsers', id: orgId }],
    }),

    // ── Bureau Intake — Drafts ────────────────────────────────────────

    createIntakeDraft: builder.mutation({
      query: (body) => ({
        url: 'intake/drafts/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['IntakeDraft', 'IntakeForm', 'IntakeAnalytics'],
    }),

    updateIntakeDraft: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `intake/drafts/${id}/`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'IntakeDraft', id },
        'IntakeForm',
        'IntakeAnalytics',
      ],
    }),

    deleteIntakeDraft: builder.mutation({
      query: (id) => ({
        url: `intake/drafts/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['IntakeDraft', 'IntakeForm', 'IntakeAnalytics'],
    }),

    fetchIntakeDraft: builder.query({
      query: (id) => `intake/drafts/${id}/`,
      providesTags: (result, error, id) => [{ type: 'IntakeDraft', id }],
    }),

    fetchIntakeDrafts: builder.query({
      query: (params = {}) => ({
        url: 'intake/drafts/',
        params: { page: params.page || 1, page_size: params.pageSize || 25 },
      }),
      providesTags: ['IntakeDraft'],
    }),

    // ── Bureau Intake — Forms ─────────────────────────────────────────

    fetchIntakeForms: builder.query({
      query: (params = {}) => ({
        url: 'intake/forms/',
        params: {
          page: params.page || 1,
          page_size: params.pageSize || 25,
          search: params.search || '',
          status: params.status || '',
          form_type: params.formType || '',
          bureau_plan: params.bureauPlan || '',
          date_range: params.dateRange || '',
        },
      }),
      providesTags: ['IntakeForm'],
    }),

    fetchAllIntakeForms: builder.query({
      query: (params = {}) => ({
        url: 'intake/forms/all/',
        params: {
          page: params.page || 1,
          page_size: params.pageSize || 25,
          search: params.search || '',
          status: params.status || '',
          form_type: params.formType || '',
          bureau_plan: params.bureauPlan || '',
          date_range: params.dateRange || '',
          user_id: params.userId || '',
        },
      }),
      providesTags: ['IntakeForm'],
    }),

    fetchIntakeFormDetails: builder.query({
      query: (id) => `intake/forms/${id}/`,
      providesTags: (result, error, id) => [{ type: 'IntakeForm', id }],
    }),

    sendIntakeFormToClient: builder.mutation({
      query: (id) => ({
        url: `intake/forms/${id}/send-to-client/`,
        method: 'POST',
      }),
      invalidatesTags: ['IntakeForm', 'IntakeDraft', 'IntakeAnalytics'],
    }),

    submitIntakeForm: builder.mutation({
      query: (body) => ({
        url: 'intake/forms/submit/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['IntakeForm', 'IntakeDraft', 'IntakeAnalytics'],
    }),

    fetchFormCreators: builder.query({
      query: () => 'intake/form-creators/',
    }),

    // ── Bureau Intake — Analytics ─────────────────────────────────────

    fetchPersonalAnalytics: builder.query({
      query: (period = 'all') => `intake/analytics/personal/${period !== 'all' ? `?period=${period}` : ''}`,
      providesTags: ['IntakeAnalytics'],
      keepUnusedDataFor: 60,
    }),

    fetchSystemAnalytics: builder.query({
      query: (period = 'all') => `intake/analytics/system/${period !== 'all' ? `?period=${period}` : ''}`,
      providesTags: ['IntakeAnalytics'],
      keepUnusedDataFor: 60,
    }),
  }),
});

export const {
  // Sales organization hooks
  useFetchMyOrganizationsQuery,
  useFetchOrgDashboardQuery,
  useFetchSalesOrgEventsQuery,
  useFetchSalesOrgEventStatsQuery,
  useFetchSalesOrgFleetQuery,
  useFetchSalesOrgDriversQuery,
  useFetchSalesDashboardSummaryQuery,
  useFetchSalesEventDetailQuery,
  useFetchSalesTeamMembersQuery,
  useAssignRepToAEMutation,
  // General Info & Contacts hooks
  useFetchOrgInfoQuery,
  useUpdateOrgContactsMutation,
  useUpdateGroupContactsMutation,
  useResetGroupContactsMutation,
  // User Management hooks
  useFetchOrgUsersQuery,
  useCreateOrgUserMutation,
  useUpdateOrgUserMutation,
  useResetUserPasswordMutation,
  // Bureau Intake hooks
  useCreateIntakeDraftMutation,
  useUpdateIntakeDraftMutation,
  useDeleteIntakeDraftMutation,
  useFetchIntakeDraftQuery,
  useFetchIntakeDraftsQuery,
  useFetchIntakeFormsQuery,
  useFetchAllIntakeFormsQuery,
  useFetchIntakeFormDetailsQuery,
  useSendIntakeFormToClientMutation,
  useSubmitIntakeFormMutation,
  useFetchFormCreatorsQuery,
  useFetchPersonalAnalyticsQuery,
  useFetchSystemAnalyticsQuery,
} = salesApiSlice;
